<template>
  <div class="profile-settings">
    <!-- User Profile Section -->
    <SettingsSectionCard
      icon="fal fa-user"
      :title="$t('user_information')"
      :subtitle="$t('user_profile_hint')"
      accent
      class="mb-4"
    >
        <v-row align="center">
          <!-- User Avatar -->
          <v-col cols="12" md="4" class="text-center">
            <v-badge
              bordered
              bottom
              color="primary"
              offset-x="50"
              offset-y="22"
              icon="fas fa-camera"
              overlap
            >
              <v-avatar color="#f5f5f5" size="140">
                <v-avatar-uploader
                  :url="urlAvatar"
                  :clickable="true"
                  field="file"
                  accept="image/jpeg,image/png,image/gif,image/svg+xml"
                  :rename="rename"
                  @failed="failed"
                  :request="requestAvatar"
                />
              </v-avatar>
            </v-badge>
            <div class="text-caption grey--text mt-2">
              {{ $t("click_to_change_avatar") }}
            </div>
          </v-col>

          <v-divider vertical class="hidden-sm-and-down" />

          <!-- User Info Display -->
          <v-col cols="12" md="8">
            <div class="profile-info-grid">
              <div class="info-row">
                <span class="info-label">{{ $t("first_name") }}</span>
                <span class="info-value">{{ user?.first_name }}</span>
              </div>
              <v-divider class="my-2"></v-divider>
              <div class="info-row">
                <span class="info-label">{{ $t("last_name") }}</span>
                <span class="info-value">{{ user?.last_name }}</span>
              </div>
              <v-divider class="my-2"></v-divider>
              <div class="info-row">
                <span class="info-label">{{ $t("email") }}</span>
                <span class="info-value">{{ user?.email }}</span>
              </div>
              <v-divider class="my-2"></v-divider>
              <div class="info-row">
                <span class="info-label">{{ $t("phone") }}</span>
                <span class="info-value">{{ user?.phone_no }}</span>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Editable Profile Fields -->
        <v-divider class="my-4"></v-divider>
        <div class="text-subtitle-2 font-weight-medium mb-3">
          {{ $t("edit_profile") }}
        </div>
        <v-row>
          <v-col cols="12" md="6">
            <TTextField
              label="first_name"
              v-model="profileForm.first_name"
              outlined
              dense
                          />
          </v-col>
          <v-col cols="12" md="6">
            <TTextField
              label="last_name"
              v-model="profileForm.last_name"
              outlined
              dense
                          />
          </v-col>
          <v-col cols="12" md="6">
            <TTextField
              label="phone"
              v-model="profileForm.phone_no"
              outlined
              dense
                          />
          </v-col>
          <v-col cols="12" md="6">
            <TTextField
              label="address"
              v-model="profileForm.address"
              outlined
              dense
                          />
          </v-col>
        </v-row>
        <div class="d-flex justify-end mt-2">
          <v-btn
            @click="updateProfile()"
            depressed
            color="primary"
            large
            :loading="savingProfile"
            class="px-8 rounded-lg"
          >
            <v-icon left small>fas fa-save</v-icon>
            {{ $t("update_profile") }}
          </v-btn>
        </div>
    </SettingsSectionCard>

    <!-- Language Selection Section -->
    <SettingsSectionCard
      icon="fal fa-globe-asia"
      :title="$t('change_language')"
      :subtitle="$t('select_your_preferred_language')"
      accent
      class="mb-4"
      body-class="pa-0"
    >
        <v-list class="py-0">
          <div v-for="(locale, index) in locales" :key="`locale-${index}`">
            <v-list-item @click="changeLocale(locale.code)">
              <v-list-item-avatar size="32" class="mr-3">
                <v-img :src="locale.flag" />
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title
                  :class="
                    locale.code === $i18n.locale
                      ? 'primary--text font-weight-bold'
                      : ''
                  "
                >
                  {{ locale.name }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-icon>
                <v-icon
                  v-if="locale.code === $i18n.locale"
                  color="primary"
                  small
                >
                  fas fa-check-circle
                </v-icon>
              </v-list-item-icon>
            </v-list-item>
            <v-divider
              v-if="index < locales.length - 1"
              class="my-0"
            ></v-divider>
          </div>
        </v-list>
    </SettingsSectionCard>

    <!-- Change Password Section -->
    <SettingsSectionCard
      icon="fal fa-key"
      :title="$t('change_password')"
      :subtitle="$t('change_password_hint')"
      accent
    >
        <v-form @submit.prevent="submitPasswordChange">
          <v-row>
            <v-col cols="12" md="4">
              <TPassword
                v-model="passwordForm.old_password"
                label="current_password"
                showKey="showOldPassword"
                :errorMessages="oldPasswordErrors"
                @showOldPassword="showOldPassword = !showOldPassword"
                :type="showOldPassword"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="4">
              <TPassword
                v-model="passwordForm.password"
                label="new_password"
                showKey="showPassword"
                :errorMessages="passwordErrors"
                @showPassword="showPassword = !showPassword"
                :type="showPassword"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="4">
              <TPassword
                v-model="passwordForm.confirm_password"
                label="confirm_password"
                showKey="showConfirmPassword"
                :errorMessages="confirmPasswordErrors"
                @showConfirmPassword="
                  showConfirmPassword = !showConfirmPassword
                "
                :type="showConfirmPassword"
                outlined
                dense
              />
            </v-col>
          </v-row>
          <div class="d-flex justify-end mt-2">
            <v-btn
              type="submit"
              depressed
              color="primary"
              large
              :loading="savingPassword"
              class="px-8 rounded-lg"
            >
              <v-icon left small>fas fa-lock</v-icon>
              {{ $t("update_password") }}
            </v-btn>
          </div>
        </v-form>
    </SettingsSectionCard>
  </div>
</template>

<script>
import TTextField from "@/components/global/TextField";
import TPassword from "@/components/global/TextPassword";
import VAvatarUploader from "@/components/VAvatarUploader";
import { mapGetters, mapActions } from "vuex";
import { setUser } from "@/controllers/auth";
import { success, error } from "@/controllers/alert";
import { uploadErrorMessage } from "@/utils/uploadError";
import { changePassword } from "@/validations/user";
import SettingsSectionCard from "@/components/settings/SettingsSectionCard.vue";

export default {
  name: "AppProfile",
  mixins: [changePassword],
  components: {
    TTextField,
    TPassword,
    VAvatarUploader,
    SettingsSectionCard,
  },
  data() {
    return {
      // Profile form
      profileForm: {
        first_name: "",
        last_name: "",
        phone_no: "",
        address: "",
      },
      savingProfile: false,
      // Password form
      passwordForm: {
        old_password: null,
        password: null,
        confirm_password: null,
      },
      showPassword: false,
      showOldPassword: false,
      showConfirmPassword: false,
      savingPassword: false,
    };
  },
  mounted() {
    // Initialize profile form
    this.profileForm = {
      first_name: this.user.first_name || "",
      last_name: this.user.last_name || "",
      phone_no: this.user.phone_no || "",
      address: this.user.address || "",
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
      locales: "auth/locales",
    }),
    urlAvatar() {
      return Help.img(this.user?.avatar);
    },
    // Password validation errors (from changePassword mixin)
    oldPasswordErrors() {
      const errors = [];
      if (!this.$v?.form?.old_password?.$dirty) return errors;
      !this.$v.form.old_password.required &&
        errors.push(
          this.$t("old_password_required") || "Current password is required",
        );
      return errors;
    },
    passwordErrors() {
      const errors = [];
      if (!this.$v?.form?.password?.$dirty) return errors;
      !this.$v.form.password.required &&
        errors.push(this.$t("password_required") || "Password is required");
      !this.$v.form.password.minLength &&
        errors.push(
          this.$t("password_min_length") ||
            "Password must be at least 6 characters",
        );
      return errors;
    },
    confirmPasswordErrors() {
      const errors = [];
      if (!this.$v?.form?.confirm_password?.$dirty) return errors;
      !this.$v.form.confirm_password.required &&
        errors.push(
          this.$t("confirm_password_required") ||
            "Confirm password is required",
        );
      !this.$v.form.confirm_password.sameAsPassword &&
        errors.push(this.$t("password_mismatch") || "Passwords do not match");
      return errors;
    },
  },
  methods: {
    ...mapActions({
      removeAuth: "auth/removeAuth",
    }),
    rename(file) {
      const ext = file.name.split(".")[1];
      return `1231232.${ext}`;
    },
    async failed(err) {
      console.error("upload failed", err);
      await error(this, uploadErrorMessage(err, this.$t.bind(this)));
    },
    changeLocale(code) {
      localStorage.setItem("locale", code);
      this.$i18n.locale = code;
    },
    // Avatar upload
    async requestAvatar(form, config) {
      const { data } = await this.$http.post("change_avatar", form, config);
      if (data.status) {
        await success(this, data.message);
        await setUser(this);
      }
    },
    // Profile update
    async updateProfile() {
      try {
        this.savingProfile = true;
        const form = {
          first_name: this.profileForm.first_name,
          last_name: this.profileForm.last_name,
          address: this.profileForm.address,
          phone_no: this.profileForm.phone_no,
        };
        const { data } = await this.$http.post("update_profile", form);
        if (data.status) {
          await setUser(this);
          await success(this, data.message);
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.savingProfile = false;
      }
    },
    // Password change
    async submitPasswordChange() {
      // Check if validation mixin is available
      if (this.$v && this.$v.form) {
        this.$v.form.$touch();
        if (this.$v.form.$invalid) {
          return;
        }
      } else {
        // Manual validation if mixin not available
        if (
          !this.passwordForm.old_password ||
          !this.passwordForm.password ||
          !this.passwordForm.confirm_password
        ) {
          await this.$swal.fire({
            icon: "error",
            title: this.$t("validation_error") || "Validation Error",
            text:
              this.$t("all_fields_required") ||
              "All password fields are required",
          });
          return;
        }
        if (this.passwordForm.password !== this.passwordForm.confirm_password) {
          await this.$swal.fire({
            icon: "error",
            title: this.$t("validation_error") || "Validation Error",
            text: this.$t("password_mismatch") || "Passwords do not match",
          });
          return;
        }
      }

      try {
        this.savingPassword = true;
        const { data } = await this.$http.post(
          "change_password",
          this.passwordForm,
        );
        if (data.status) {
          await success(this, data.message);
          await this.removeAuth();
          this.$router.push("/login");
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.savingPassword = false;
      }
    },
  },
};
</script>

<style scoped>
.profile-settings {
  max-width: 900px;
}

.profile-info-grid {
  padding: 8px 16px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.info-label {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;
}

.info-value {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  text-align: right;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .profile-info-grid {
    padding: 8px;
  }

  .hidden-sm-and-down {
    display: none !important;
  }
}
</style>

<style>


.theme--dark .profile-settings .info-label {
  color: rgba(255, 255, 255, 0.7);
}

.theme--dark .profile-settings .info-value {
  color: rgba(255, 255, 255, 0.87);
}
</style>
